import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { makeComponentInstance } from '../../core/library/loader.js';
import { renderComponent } from './component-renderer.js';

/**
 * Genera una thumbnail PNG (data URL) para un `ComponentDef`.
 *
 * Se pinta una instancia por defecto del componente en un stage Konva
 * temporal, se calcula su caja envolvente, se encaja con margen y se
 * exporta como PNG. Se usa el mismo renderer que en el lienzo real,
 * así que la miniatura refleja fielmente el aspecto del componente.
 *
 * El resultado se cachea (in-memory + localStorage) mediante la clase
 * {@link ComponentPreviewCache} definida más abajo, pero esta función
 * puede llamarse por separado (sin cache) para generar la primera vez.
 *
 * @param def       Definición del componente.
 * @param sizePx    Lado (en pixels) del cuadrado de salida.
 * @param pixelRatio Densidad de pixels para nitidez.
 */
export function renderComponentPreview(
  def: ComponentDef,
  sizePx = 72,
  pixelRatio = 2,
): string {
  // Instancia temporal por defecto. Para que el CUERPO ocupe lo más
  // grande posible en la miniatura, recortamos los pines a 1 segmento
  // (~1 paso de rejilla). Los pines ya cortos (DIP, etc.) se dejan
  // intactos.
  const inst = makeComponentInstance(def);
  const MAX_PREVIEW_SEGMENTS = 1;
  for (let i = 0; i < def.pins.length; i++) {
    const pinDef = def.pins[i]!;
    const pinState = inst.pins[i];
    if (!pinState) continue;
    // cut_at_index = k → path tiene k+1 segmentos. Para 1 segmento,
    // k = 0. Sólo recortamos si el pin original tiene MÁS.
    if (pinDef.bend_points_count + 1 > MAX_PREVIEW_SEGMENTS) {
      pinState.cut_at_index = MAX_PREVIEW_SEGMENTS - 1;
    }
  }
  // Contenedor offscreen que Konva necesita.
  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
  document.body.appendChild(container);
  try {
    const stage = new Konva.Stage({ container, width: sizePx, height: sizePx });
    const layer = new Konva.Layer({ listening: false });
    stage.add(layer);
    const node = renderComponent(inst, def, false);
    node.listening(false);
    layer.add(node);
    layer.draw();

    // Bounding box en coords mundo (Konva lo devuelve en px del stage
    // por defecto, y como no le hemos aplicado escala aún, 1 unidad
    // mundo = 1 pixel — luego escalamos).
    const box = node.getClientRect({ relativeTo: layer });
    if (!box || box.width <= 0 || box.height <= 0) {
      stage.destroy();
      return blankPng(sizePx);
    }
    // Escala para que quepa con margen. El margen anterior (4 px sobre
    // 72) dejaba casi ningún aire y algunos componentes que rebasan
    // ligeramente su bounding box (elipses, sombras) salían tocados
    // por arriba o por los lados. Con 10 px queda un colchón suficiente.
    const margin = 10; // px
    const usable = sizePx - margin * 2;
    const scale = Math.min(usable / box.width, usable / box.height);
    stage.scale({ x: scale, y: scale });
    // Centrar la caja envolvente en el stage.
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    stage.position({
      x: sizePx / 2 - cx * scale,
      y: sizePx / 2 - cy * scale,
    });
    layer.batchDraw();

    const url = stage.toDataURL({
      pixelRatio,
      mimeType: 'image/png',
      x: 0,
      y: 0,
      width: sizePx,
      height: sizePx,
    });
    stage.destroy();
    return url;
  } finally {
    container.remove();
  }
}

function blankPng(sizePx: number): string {
  const c = document.createElement('canvas');
  c.width = sizePx;
  c.height = sizePx;
  return c.toDataURL('image/png');
}

/**
 * Cache de miniaturas de componentes.
 *
 * - Nivel 1: in-memory (Map) para acceso instantáneo durante la sesión.
 * - Nivel 2: `localStorage`, versionado, para persistir entre sesiones
 *   sin tener que re-renderizar.
 *
 * Cada entrada se identifica por `defId` + un breve hash del contenido
 * de la definición (para que un cambio en la def invalide su preview
 * sin tener que subir manualmente `CACHE_VERSION`).
 */
export class ComponentPreviewCache {
  private static readonly STORAGE_PREFIX = 'deadbug:preview:v8:';
  private readonly memory = new Map<string, string>();

  /** Devuelve la miniatura o la genera y cachea. */
  get(def: ComponentDef, sizePx = 72): string {
    const key = this.keyFor(def, sizePx);
    const hit = this.memory.get(key);
    if (hit) return hit;
    // localStorage.
    try {
      const stored = localStorage.getItem(ComponentPreviewCache.STORAGE_PREFIX + key);
      if (stored) {
        this.memory.set(key, stored);
        return stored;
      }
    } catch {
      // localStorage puede estar deshabilitado (modo privado, cuota).
    }
    // Render en caliente.
    const url = renderComponentPreview(def, sizePx);
    this.memory.set(key, url);
    try {
      localStorage.setItem(ComponentPreviewCache.STORAGE_PREFIX + key, url);
    } catch {
      // Cuota llena — se conserva sólo en memoria.
    }
    return url;
  }

  /** Borra toda la cache (memoria + persistente). */
  clear(): void {
    this.memory.clear();
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(ComponentPreviewCache.STORAGE_PREFIX)) toRemove.push(k);
      }
      for (const k of toRemove) localStorage.removeItem(k);
    } catch {
      // Ignorar.
    }
  }

  private keyFor(def: ComponentDef, sizePx: number): string {
    return `${def.id}@${sizePx}#${shortHash(JSON.stringify(def))}`;
  }
}

/**
 * Hash muy simple (DJB2 truncado a 32 bits) para invalidar entradas
 * cuando cambia el contenido de la def. No pretende ser criptográfico:
 * sólo tiene que ser rápido, determinista y estable entre sesiones.
 */
function shortHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}
